/*
 * @providesModule LiveBracketDataController
 *
 */

import React from 'react';
import {Alert, Platform, Text, Vibration} from 'react-native';

import styles from './LiveBracketDataController.styles';
import {DialogV2} from '@skillz-rn/atoms';
import {CountdownTimer} from '@skillz/ui';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {_m} from 'INTL';
import ModalNavigationController from 'ModalNavigationController';
import PlayableMatchesStore from 'PlayableMatchesStore';
import LinearGradient from 'react-native-linear-gradient';
import {CountdownState} from 'RewardSystemsTypes';
import SkillzImage from 'SkillzImage';
import SKZTournamentCategories from 'SKZTournamentCategories';
import SKZTournamentTemplateSet from 'SKZTournamentTemplateSet';
import Tournament from 'SKZTournament';
import LiveBracketAutoplayDialog from 'LiveBracketAutoplayDialog';
import LiveBracketToastController from 'LiveBracketToastController';
import {Navigator} from 'react-native-deprecated-custom-components';
import SKZMatchPlayer from 'SKZMatchPlayer';

/**
 * This function is used to determine if the user is in an important live bracket. An important
 * Live Bracket is defined as a live bracket the user is registered for, with the following conditions:
 * - The live bracket is starting soon (determined by client prop)
 * - The user is still participating in the live bracket
 * - However, if we're trying to tap on the live bracket we're registered for, we should allow it
 * - If the tapped tournament starts more than live_bracket_prevent_join_secs before the live bracket's Round 1, allow it
 */
export const getImportantLiveBracket = async (
  tappedTournament?: Tournament | SKZTournamentTemplateSet
) => {
  if (!ClientPropertyConstants.getLiveBracketsV1Enabled()) {
    return null;
  }

  /**
   * Cases where the live bracket is not important:
   * - The tournament is not a live bracket
   * - The player is not registered for the live bracket
   * - The tournament tapped on is the live bracket the player is registered for
   * - If the live bracket is upcoming, but too far away
   * - If the tapped tournament starts more than live_bracket_prevent_join_secs before the live bracket's Round 1
   *
   * Cases where the live bracket is important:
   * - The live bracket is starting soon
   * - The player is currently in the live bracket
   */

  const soonestRegisteredLiveBracket = await LiveBracketToastController.getLiveBracket(true);

  if (!soonestRegisteredLiveBracket) {
    return null;
  }

  const isTheTappedTournament =
    tappedTournament && tappedTournament.id === soonestRegisteredLiveBracket.id;

  // If tapping on the same tournament we're registered for, allow it
  if (isTheTappedTournament) {
    return null;
  }

  // If user has finished the final round, allow further joins
  if (soonestRegisteredLiveBracket.hideBottomSheetAndAllowFurtherJoins()) {
    return null;
  }

  // Check time-based restriction for non-live bracket tournaments
  if (
    tappedTournament &&
    tappedTournament instanceof Tournament &&
    !tappedTournament.isLiveBracket()
  ) {
    const currentTime = new Date().getTime();
    const liveBracketRound1StartTime = new Date(soonestRegisteredLiveBracket.start).getTime();
    const timeUntilLiveBracket = liveBracketRound1StartTime - currentTime;
    const preventJoinSecsMs = ClientPropertyConstants.getLiveBracketPreventJoinSecs() * 1000;

    // If the live bracket starts in more than live_bracket_prevent_join_secs from now, allow the join
    if (timeUntilLiveBracket > preventJoinSecsMs) {
      return null;
    }
  }

  return soonestRegisteredLiveBracket;
};

/**
 * This function is used to render the live bracket dialog when the user is in an important
 * live bracket. It should be paired with getImportantLiveBracket to block the user from doing
 * the typical onPress action on the Play Tab and render the dialog instead.
 */
export const renderLiveBracketDialog = (liveBracket: Tournament) => {
  const hasLiveBracketStarted = liveBracket.hasTournamentStarted();
  const dialog = (
    <DialogV2
      title={
        hasLiveBracketStarted
          ? _m('ANOTHER LIVE BRACKET ONGOING')
          : _m('ANOTHER TOURNAMENT STARTING SOON')
      }
      ctaText={_m('OK')}
      ctaAction={() => {
        ModalNavigationController.hideModal();
      }}
      shouldShowRibbon={true}
      icon={
        <SkillzImage
          resizeMode={'contain'}
          source={'ic_live_brackets_cup.png'}
          style={{width: 125, height: 125}}
        />
      }
      descriptionView={renderDialogDescription(liveBracket)}
    />
  );

  ModalNavigationController.showModal(dialog, {
    animationType: 'fade',
    transparent: true,
  });
};

/**
 * This function renders the description for the live bracket dialog.
 */
const renderDialogDescription = (liveBracket: Tournament) => {
  const relevantDate = liveBracket.hasTournamentStarted()
    ? liveBracket.bracket?.user_expires_at
    : liveBracket.start;

  const countdownDate = new Date(relevantDate ?? -1);
  return (
    <>
      <Text style={styles.liveBracketDialogText}>
        {/* TODO: Update copy depending on user state of live bracket */}
        {_m('You have already registered with one tournament. That tournament game will begin in:')}
      </Text>
      <LinearGradient
        style={styles.liveBracketDialogTimerContainer}
        colors={['#A101A0', '#FF00B5']}
      >
        <CountdownTimer
          toDate={countdownDate}
          stateType={CountdownState.ending}
          labelStyling={{
            ...styles.liveBracketDialogTimerLabelStyling,
            size: 'M',
            textStyle: {
              fontSize: 12,
            },
          }}
          expiredLabelText={_m('Starting soon')}
        />
      </LinearGradient>
    </>
  );
};

export const _vibrateForAutoplay = () => {
  const pattern = Platform.OS === 'ios' ? [0, 400, 400] : [0, 400, 300, 400];
  Vibration.vibrate(pattern);
};

/**
 * This function is used to render the live bracket auto-play dialog when the user is
 * ready to play the match.
 */
export const renderLiveBracketAutoplayDialog = (tournament: Tournament) => {
  // Hide any existing modals
  if (ModalNavigationController.isModalShown()) {
    ModalNavigationController.hideModal();
  }

  // Trigger vibration for autoplay
  _vibrateForAutoplay();

  ModalNavigationController.showModal(<LiveBracketAutoplayDialog tournamentData={tournament} />, {
    animationType: 'fade',
    transparent: true,
  });
};

export const isLiveBracketTournamentReadyToAutoplay = (
  tournament: Tournament,
  mainNavigator?: Navigator,
  useBracketPlayerState?: boolean,
  bracketPlayerState?: string
): boolean => {
  if (!ClientPropertyConstants.getLiveBracketsV1Enabled()) {
    return false;
  }

  if (useBracketPlayerState) {
    if (bracketPlayerState !== 'READY_TO_JOIN') {
      return false;
    }
  } else {
    if (!tournament?.isLiveBracket() || !tournament?.isPlayerMatchReadyToPlay()) {
      return false;
    }
  }

  if (!mainNavigator) {
    return false;
  }

  return true;
};

/**
 * This function is used to get the registered live brackets for the user.
 */
export const getRegisteredLiveBrackets = async (useCache = false) => {
  try {
    const categories = await PlayableMatchesStore.fetchTournamentCategories(useCache);
    const tournamentCategories = new SKZTournamentCategories({categories});

    const liveCategory = tournamentCategories.getCategoryForType('LIVE_BRACKET');
    return (
      liveCategory?.tournaments?.filter(
        (tournament: Tournament) => tournament.isLiveBracket() && tournament.isPlayerRegistered()
      ) || []
    );
  } catch (error) {
    console.debug('LiveBracketDataController: Error getting registered live brackets', error);
    return [];
  }
};

/**
 * This function verifies if player can register to the tournament,
 * if not, show an alert with the reason and returns false.
 */
export const canRegisterForLiveBracketTournament = async (
  tournamentData: Tournament
): Promise<boolean> => {
  if (!ClientPropertyConstants.getLiveBracketsV1Enabled()) {
    return Promise.reject(new Error('Live brackets V1 is not enabled'));
  }

  if (!tournamentData) {
    return Promise.reject(new Error('No tournament found'));
  }

  // Check if registration is currently available based on new timing properties
  if (!tournamentData.isRegistrationAvailable()) {
    return Promise.reject(new Error('Registration is not yet available for this live bracket'));
  }

  const registeredLiveBrackets = await getRegisteredLiveBrackets(false);
  const overlappingTournaments = registeredLiveBrackets.filter((tournament: Tournament) => {
    // If user has finished the final round, allow further joins
    if (tournament.hideBottomSheetAndAllowFurtherJoins()) {
      return false;
    }

    // For live bracket vs live bracket registration, use the existing overlap logic
    if (tournamentData.isLiveBracket()) {
      return tournamentData.hasOverlappingLiveBracket(tournament);
    }

    // For non-live bracket vs live bracket, check time-based restriction
    const currentTime = new Date().getTime();
    const liveBracketRound1StartTime = new Date(tournament.start).getTime();
    const timeUntilLiveBracket = liveBracketRound1StartTime - currentTime;
    const preventJoinSecsMs = ClientPropertyConstants.getLiveBracketPreventJoinSecs() * 1000;

    // If the live bracket starts in less than live_bracket_prevent_join_secs from now, block the registration
    return timeUntilLiveBracket <= preventJoinSecsMs;
  });

  if (overlappingTournaments?.length) {
    const message = _m(
      `You have already registered for the live bracket "${overlappingTournaments[0]?.name}", which may overlap with this one.`
    );

    Alert.alert(_m('Registration Unsuccessful'), message);

    return Promise.reject(new Error(message));
  }

  return true;
};

/**
 * Determines if the win percentage should be shown for the player in the Versus Screen.
 */
export const shouldDisplayWinPercentTag = (
  tournament?: Tournament,
  matchPlayer?: SKZMatchPlayer,
  userId?: string
): boolean => {
  if (!tournament || !matchPlayer || !userId) {
    console.debug('LiveBracketDataController: shouldDisplayWinPercentTag - Missing required data');
    return false;
  }

  if (!tournament?.isLiveBracket()) {
    console.debug(
      'LiveBracketDataController: shouldDisplayWinPercentTag - Tournament is not a live bracket'
    );
    return false;
  }

  if (!ClientPropertyConstants.getLiveBracketsV1Enabled()) {
    console.debug(
      'LiveBracketDataController: shouldDisplayWinPercentTag - Live brackets V1 is not enabled'
    );
    return false;
  }

  return true;
};
